import { CancelledError, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import {
  agentToolsApi,
  type MCPToolSharing,
} from "../../../services/agent-tools-service";
import { mcpQueryKeys } from "../query-keys";
import { canNotifyMCPMutation, useScopedMCPMutation } from "./useScopedMCPMutation";
import { mcpRequestOptions } from "./useMCPQueryScope";

type ApiError = {
  response?: {
    data?: { error?: { message?: string }; detail?: string; message?: string };
  };
  message?: string;
};

const extractErrorMessage = (error: ApiError, fallback: string): string => {
  const data = error.response?.data;
  return (
    data?.error?.message ||
    data?.detail ||
    data?.message ||
    error.message ||
    fallback
  );
};

type Variables = {
  connectionId: string;
  toolId: string;
  sharing: MCPToolSharing;
};

type Options = {
  onSuccess?: () => void;
  isSessionCurrent?: () => boolean;
};

export const useUpdateToolSharing = (options: Options = {}) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  return useScopedMCPMutation<void, Variables, undefined, ApiError>({
    mutationFn: ({ connectionId, toolId, sharing }, scope) => {
      if (options.isSessionCurrent && !options.isSessionCurrent()) return Promise.reject(new CancelledError());
      return agentToolsApi.updateMCPToolSharing(connectionId, toolId, sharing, mcpRequestOptions(scope));
    },
    onSuccess: (_data, { connectionId }, scope) => {
      const notify = canNotifyMCPMutation(scope) && (options.isSessionCurrent?.() ?? true);
      if (notify) toast.success(t("mcpPage.mutation.toolAccessUpdated"));
      queryClient.invalidateQueries({
        queryKey: mcpQueryKeys.tools(scope.key, connectionId),
      });
      if (notify) options.onSuccess?.();
    },
    onError: (error: ApiError, _variables, _context, scope) => {
      if (!canNotifyMCPMutation(scope, error) || !(options.isSessionCurrent?.() ?? true)) return;
      toast.error(extractErrorMessage(error, t("mcpPage.mutation.toolAccessFailed")));
    },
  });
};
